import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSession } from './sessionContext';
import { prefetchBoardIdentity } from '../services/voice/boardIdentityStore';

// Console shell state. Role + tenant are derived from the REAL platform session
// (/v1/me) — role from the JWT slug, tenant from the signed-in org. The "View as"
// role switcher remains as an explicit override (a platform_admin may impersonate
// operator/viewer to see what a customer sees).

export type Role = 'softknack' | 'operator' | 'viewer';
export type ScreenKey =
  | 'onboard'
  | 'home' | 'board' | 'live' | 'log' | 'followups' | 'analytics'
  | 'intents' | 'settings'
  | 'catalog' | 'router' | 'providers' | 'phone'
  // Outbound capability (SKVOICE-OUTBOUND-001)
  | 'outgoing' | 'campaigns' | 'obrules' | 'dnd' | 'inbound' | 'obreasons';

export interface Tenant { name: string; mark: string; licenses: string[]; vertical: string | null; }

// Minimal call-header shape the Call Detail overlay needs (subset of CallRow).
// Carried through openCall so the overlay renders the REAL selected call's header
// (caller/dir/outcome/duration) instead of looking it up in a mock array.
export interface SelectedCallHeader { id: string; contact: string; dir: string; outcome: string; durSec: number; }

// Verbatim from the HTML TENANTS.
export const TENANTS: Tenant[] = [
  { name: 'Acme Clinic', mark: 'AC', licenses: ['calendar', 'crm', 'billing'], vertical: null },
  { name: 'Zaffran Dine', mark: 'ZD', licenses: ['calendar', 'crm', 'tickets'], vertical: 'hospitality' },
];

// Verbatim from the HTML NAVDEF — [id, label, icon-key].
export const NAVDEF: Record<Role, { group: string; items: [ScreenKey, string, string][] }[]> = {
  softknack: [
    { group: 'Setup', items: [['catalog', 'Intent Catalog', 'v-grid'], ['router', 'Router Prompt', 'v-route'], ['providers', 'Providers', 'v-chip'], ['phone', 'Phone & KYC', 'v-sliders'], ['obreasons', 'Outbound Reasons', 'v-flag']] },
  ],
  operator: [
    // "Start here" — the guided setup. (Shown while setup is incomplete; the
    // completion-gate that hides it lands with setup-state detection.)
    { group: 'Start here', items: [['onboard', 'Set up SK Voice', 'v-sliders']] },
    { group: 'Overview', items: [['home', 'Home', 'v-home'], ['board', 'Dashboard', 'v-grid'], ['live', 'Live Calls', 'v-pulse'], ['log', 'Call Log', 'v-list'], ['followups', 'Follow-ups', 'v-flag'], ['analytics', 'Analytics', 'v-chart']] },
    { group: 'Outbound', items: [['outgoing', 'Outgoing', 'v-route'], ['campaigns', 'Campaigns', 'v-pulse'], ['obrules', 'Outbound Rules', 'v-sliders']] },
    { group: 'Inbound', items: [['inbound', 'Inbound Rules', 'v-sliders']] },
    { group: 'Controls', items: [['intents', 'Intents', 'v-grid'], ['dnd', 'DND & Blocked', 'v-flag'], ['settings', 'Agent Settings', 'v-sliders']] },
  ],
  viewer: [
    { group: 'Overview', items: [['home', 'Home', 'v-home'], ['board', 'Dashboard', 'v-grid'], ['live', 'Live Calls', 'v-pulse'], ['log', 'Call Log', 'v-list'], ['followups', 'Follow-ups', 'v-flag'], ['analytics', 'Analytics', 'v-chart']] },
    { group: 'Outbound', items: [['outgoing', 'Outgoing', 'v-route']] },
  ],
};

const DEFSCREEN: Record<Role, ScreenKey> = { softknack: 'catalog', operator: 'home', viewer: 'home' };

// Screen title + subtitle map (from the HTML `titles`).
export const TITLES: Record<ScreenKey, [string, string]> = {
  onboard: ['Set up SK Voice', 'Get your AI receptionist live'],
  home: ['Home', 'Today at a glance'],
  board: ['Live Dashboard', 'Real-time · your organisation'],
  live: ['Live Calls', 'Real-time monitor · SSE'],
  log: ['Call Log', 'Every completed call, filterable'],
  followups: ['Follow-ups', 'The fallback floor — open items to action'],
  analytics: ['Outcomes & Analytics', 'Resolution, escalation, CSAT, metering'],
  intents: ['Intents', 'Toggle what your agent can do'],
  settings: ['Agent Settings', 'Persona, hours, escalation & policy'],
  catalog: ['Intent Catalog', 'Global · Softknack authored'],
  router: ['Router Prompt', 'Global orchestration prompt · versioned'],
  providers: ['Provider & Pipeline', 'Per-organization configuration'],
  phone: ['Phone & KYC', 'Setup tier · link an approved KYC to a SoftKnack workspace'],
  outgoing: ['Outgoing', 'When SK Voice calls out — and why'],
  campaigns: ['Campaigns', 'Bulk promotional & marketing calls'],
  obrules: ['Outbound Rules', 'Controls tier · windows, caps, retries'],
  dnd: ['DND & Blocked', 'Suppression list · incoming & outgoing'],
  inbound: ['Inbound Rules', 'How SK Voice answers incoming calls'],
  obreasons: ['Outbound Reasons', 'Setup tier · Softknack-managed reason registry'],
};

interface Ctx {
  role: Role;
  /** Raw platform role driving `role` (platform_admin | org_admin | admin | user).
   *  Screens/actions gate on this + `can()` — never on a mock override. */
  platformRole: string | null;
  /** Scope check from the real session (e.g. can('billing:write')). */
  can: (scope: string) => boolean;
  screen: ScreenKey;
  // Real signed-in org id (from /v1/me) — screens use it as the refetch key so a
  // real org switch reloads their data. null until the session resolves.
  orgId: string | null;
  tenant: Tenant;
  /** True once org.metadata.setup.completed_at is set — hides the "Set up SK
   *  Voice" nav item and any finish-setup nudge. */
  setupComplete: boolean;
  go: (s: ScreenKey) => void;
  // Call Detail overlay (history or live).
  selectedCall: string | null;
  selectedCallHeader: SelectedCallHeader | null;
  detailKind: 'history' | 'live' | null;
  openCall: (id: string, header?: SelectedCallHeader) => void;
  openLive: (id: string, header?: SelectedCallHeader) => void;
  closeCall: () => void;
  // 1s tick — live-call durations count up off this (matches the HTML liveTick).
  liveTick: number;
  // Sidebar collapsed to an icon-only rail (persisted). Toggle from the sidebar
  // chevron or the topbar hamburger — lets two RLS windows sit side by side.
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const VoiceConsoleContext = createContext<Ctx | null>(null);

// Real session → console Role tier. platform_admin (a platform-scoped role, NOT
// a membership role) always wins → Setup. Otherwise the active-org membership
// role: org_admin/admin → Operator; user (or anything else) → read-only Viewer.
// Note the finer org_admin-vs-admin split is enforced by scopes (can()), not here.
function roleTier(isPlatformAdmin: boolean, membershipRole: string | null): Role {
  if (isPlatformAdmin) return 'softknack';
  if (membershipRole === 'org_admin' || membershipRole === 'admin') return 'operator';
  return 'viewer';
}
// Signed-in org (from /v1/me) → the console Tenant shape. vertical + products now
// come from /v1/me, so the Setup Guide can take the vertical-aware short path and
// screens can gate on entitlements. No mock fallback — an absent org shows a
// truthful placeholder, never a fabricated demo org.
function tenantFromOrg(org: { name?: string; vertical?: string | null } | null, products: string[]): Tenant {
  const label = org?.name?.trim() || '—';
  const mark = label === '—' ? '—' : (label.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '—');
  return { name: label, mark, licenses: products, vertical: org?.vertical ?? null };
}

export function VoiceConsoleProvider({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const [screen, setScreen] = useState<ScreenKey>('home');
  const [selectedCall, setSelectedCall] = useState<string | null>(null);
  const [selectedCallHeader, setSelectedCallHeader] = useState<SelectedCallHeader | null>(null);
  const [detailKind, setDetailKind] = useState<'history' | 'live' | null>(null);
  const [liveTick, setLiveTick] = useState(0);
  // Persisted sidebar state (web localStorage; guarded for native/SSR).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('sk_sidebar_collapsed') === '1'; }
    catch { return false; }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sk_sidebar_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setLiveTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // The console role is derived ONLY from the real session — no impersonation
  // override. platform_admin → Setup; org_admin/admin → Operator; user → Viewer.
  const role: Role = roleTier(session.isPlatformAdmin, session.role);
  const platformRole = session.isPlatformAdmin ? 'platform_admin' : session.role;

  // Tenant = the real signed-in org from /v1/me. No mock fallback: identity is a
  // real API, so this is always the live org (or a truthful "—" placeholder).
  const tenant: Tenant = tenantFromOrg(session.org, session.products);
  const orgId: string | null = session.org?.id ?? null;
  const setupComplete = !!session.org?.setup?.completed_at;

  // Warm the board header (org name + dial-in) the moment the session resolves,
  // rather than when the user opens Dashboard. The read is cookie-scoped, so it
  // needs nothing from here but the org key — starting it now means the number
  // is already in hand by the time the board mounts.
  useEffect(() => { prefetchBoardIdentity(orgId); }, [orgId]);

  // If the current screen isn't allowed for this role, snap to the role's home
  // (defense-in-depth: a Viewer can't linger on a Setup screen).
  useEffect(() => {
    const allowed = new Set(NAVDEF[role].flatMap(g => g.items.map(([id]) => id as ScreenKey)));
    if (!allowed.has(screen)) setScreen(DEFSCREEN[role]);
  }, [role, screen]);

  const go = useCallback((s: ScreenKey) => { setScreen(s); setSelectedCall(null); setSelectedCallHeader(null); setDetailKind(null); }, []);
  const openCall = useCallback((id: string, header?: SelectedCallHeader) => { setSelectedCall(id); setSelectedCallHeader(header ?? null); setDetailKind('history'); }, []);
  const openLive = useCallback((id: string, header?: SelectedCallHeader) => { setSelectedCall(id); setSelectedCallHeader(header ?? null); setDetailKind('live'); }, []);
  const closeCall = useCallback(() => { setSelectedCall(null); setSelectedCallHeader(null); setDetailKind(null); }, []);

  return (
    <VoiceConsoleContext.Provider
      value={{ role, platformRole, can: session.can, screen, orgId, tenant, setupComplete, go,
        selectedCall, selectedCallHeader, detailKind, openCall, openLive, closeCall, liveTick,
        sidebarCollapsed, toggleSidebar }}
    >
      {children}
    </VoiceConsoleContext.Provider>
  );
}

export function useConsole(): Ctx {
  const ctx = useContext(VoiceConsoleContext);
  if (!ctx) throw new Error('useConsole must be used within VoiceConsoleProvider');
  return ctx;
}
